import { SeverityType } from '@/services/warnings/warningDefinitions';
import { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { StoreApi, createStore, useStore } from 'zustand';
import { getRosterWarnings } from '../../../services/warnings/getRosterWarnings';
import { useBaseWarningProps } from '../../../services/warnings/useBaseWarningProps';
import { useBuildEmployeesForShift } from '../hooks/useEmployeesForShift';
import { useWorkingRoster } from './WorkingRosterService';
import { RosterWarningModel } from '@/services/warnings/warnings.types';

export interface RosterWarningsStore {
  warnings: RosterWarningModel[];
}

export const RosterWarningsContext = createContext<StoreApi<RosterWarningsStore> | null>(null);

export const RosterWarningsProvider: FC<PropsWithChildren> = ({ children }) => {
  const baseProps = useBaseWarningProps();
  const buildDropdownOptions = useBuildEmployeesForShift();
  const { workingRoster } = useWorkingRoster();

  const store = useMemo(() => {
    return createStore<RosterWarningsStore>()(() => ({
      warnings: !workingRoster
        ? []
        : getRosterWarnings({
            ...baseProps,
            workingRoster,
            buildEmployeeOptions: buildDropdownOptions
          })
    }));
  }, [baseProps, workingRoster, buildDropdownOptions]);

  return <RosterWarningsContext.Provider value={store}>{children}</RosterWarningsContext.Provider>;
};

type SelectorReturn<S extends (s: RosterWarningsStore) => any> = ReturnType<S>;
export function useRosterWarningsService<S extends (s: RosterWarningsStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(RosterWarningsContext);
  if (!context) {
    throw new Error('useRosterWarnings must be used within a RosterWarningsProvider');
  }
  return useStore(context, selector);
}

export const useIsRosterValid = () => {
  const warnings = useRosterWarningsService(s => s.warnings);

  return !warnings.find(w => w.severity === SeverityType.CRITICAL);
};

export const useWarningsForShift = (shiftId: string) => {
  const warnings = useRosterWarningsService(s => s.warnings);

  return warnings.filter(w => w.shiftId === shiftId);
};

export const useAllWarnings = () => {
  const warnings = useRosterWarningsService(s => s.warnings);

  return warnings;
}
